import { Link } from 'react-router-dom';
import { Card, linkClassName, primaryLinkButtonClassName, secondaryTextClassName } from '@monecolier/ui';

export interface AccessDeniedProps {
  // `onLogout` plutôt qu'un appel interne à useAuth().logout() : l'écran est affiché
  // via un état LOCAL (`accessDenied`) à Login.tsx/Register.tsx, que logout() seul ne
  // réinitialiserait pas — un logout() interne aurait vidé le contexte auth sans
  // jamais faire revenir l'utilisateur au formulaire (bug réel constaté en navigateur
  // réel : le bouton restait sans effet visible). L'appelant compose les deux.
  onLogout: () => void;
  // AUT-25 : `true` uniquement si `GET /me#revoked` l'affirme — jamais déduit
  // localement. Bascule vers la Variante 2 (ton factuel, déconnexion SEULE,
  // AUCUN CTA de création — qui contournerait la révocation).
  revoked?: boolean;
}

// UX-59 : `primaryLinkButtonClassName` (packages/ui/src/styles.ts), extraite
// d'ici — CTA de navigation devant ressembler à `Button` variant="primary"
// sans être un <button> (voir styles.ts pour le détail).

// AUT-03 : écran affiché après une connexion RÉUSSIE (identifiants valides) mais dont
// le compte n'a aucun Membership — donc aucun rôle/tenant résolvable. Volontairement
// web-admin uniquement : voir Login.tsx/Register.tsx et docs/architecture.md, section
// "AUT-03", pour pourquoi web-parent n'a pas (encore) l'équivalent.
//
// SCH-01 : ce compte SANS Membership est EXACTEMENT celui qui peut créer un
// établissement en self-serve (POST /schools, voir docs/architecture.md, section
// "SCH-01") — ce lien est donc le chemin normal vers /schools/new, pas une simple
// impasse avec juste une déconnexion.
//
// AUT-22 : ce ticket avait cherché DEUX variantes de contenu (compte tout neuf
// vs. Membership révoqué) mais conclu, en Étape 0, qu'AUCUN signal fiable
// n'existait en base pour les distinguer (`Membership` supprimée par une
// SUPPRESSION DURE, AUT-13, sans trace) — un seul message accueillant (Variante
// 1) avait donc été construit, la Variante 2 restant un écran mort faute de
// signal.
//
// AUT-25 lève ce blocage : `POST .../revoke` (routes/members.ts) écrit désormais
// une trace SYNCHRONE (`RevokedMembership`) dans la MÊME transaction que la
// suppression — `GET /me#revoked` l'expose, `prop revoked` ci-dessus en découle
// directement, jamais recalculé ici. Variante 1 (compte neuf) INCHANGÉE.
//
// AUT-24 : le lien "👤 Compte" ajouté ICI par AUT-22 (point 4 du ticket) était
// une VRAIE duplication, trouvée en vérification staging — `<Sidebar />`
// (App.tsx) enveloppe TOUTES les routes, y compris `/profile`, et son
// `footerLinks` (voir components/Sidebar.tsx) rend DÉJÀ un lien "Compte" vers
// `/account`, gardé UNIQUEMENT par `isAuthenticated` (jamais par un rôle) —
// donc TOUJOURS présent sur cet écran précis, avant même ce ticket. Retiré ici,
// jamais dupliqué — le lien de la Sidebar persistante suffit.
export function AccessDenied({ onLogout, revoked = false }: AccessDeniedProps) {
  const logoutButton = (
    <button
      type="button"
      onClick={onLogout}
      className={`${linkClassName} self-center text-center`}
      data-testid="access-denied-logout"
    >
      Se déconnecter
    </button>
  );

  if (revoked) {
    // Ton FACTUEL, jamais alarmant ni accusateur — même discipline "jamais
    // d'alarme sur un état normal du produit" déjà établie ailleurs (PAY-01,
    // UX-40) — mais AUCUN CTA de création : en proposer un contournerait la
    // révocation, contrairement à la Variante 1 ci-dessous.
    return (
      <Card title="Accès à ce portail">
        <div className="flex flex-col items-center gap-3 text-center">
          <span aria-hidden="true" className="text-4xl">
            🔒
          </span>
          <p data-testid="access-denied-message" className={secondaryTextClassName}>
            Votre accès à cet établissement a été révoqué. Contactez l&rsquo;administrateur de votre école si vous
            pensez qu&rsquo;il s&rsquo;agit d&rsquo;une erreur.
          </p>
        </div>
        {logoutButton}
      </Card>
    );
  }

  return (
    <Card title="Bienvenue sur Mon Ecolier !">
      <div className="flex flex-col items-center gap-3 text-center">
        <span aria-hidden="true" className="text-4xl">
          🎉
        </span>
        <p data-testid="access-denied-message" className={secondaryTextClassName}>
          Il ne vous reste qu&rsquo;une étape avant de commencer : créer votre établissement.
        </p>
      </div>
      <Link to="/schools/new" className={primaryLinkButtonClassName} data-testid="access-denied-create-school-link">
        Créer mon établissement
      </Link>
      {logoutButton}
    </Card>
  );
}
